// inspired by:
// https://m3.material.io/components/buttons/specs
// https://react-spectrum.adobe.com/react-aria/examples/ripple-button.html
// https://www.geeksforgeeks.org/how-to-create-a-ripple-effect-on-click-the-button/

import {useEffect, useRef, useState, memo, ButtonHTMLAttributes} from "react";

export interface RippleButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Variant of the button.
   */
  variant?:
    | "primary"
    | "secondary"
    | "icon"
    | "white"
    | "facebook"
    | "outlined"
    | "text";

  /**
   * Whether the button is disabled.
   */
  disabled?: boolean;

  /**
   * Whether the button is pending.
   */
  isPending?: boolean;

  /**
   * Whether the text is centered.
   */
  centerText?: boolean;

  /**
   * Whether the ripple effect is disabled.
   */
  disabledRippleEffect?: boolean;
}

const RippleButton = ({
  type = "button",
  variant = "primary",
  className,
  disabled,
  isPending,
  onClick,
  children,
  centerText = false,
  disabledRippleEffect = false,
  ...rest
}: RippleButtonProps) => {
  const [coords, setCoords] = useState<{x: number; y: number} | null>(null);
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setCoords({x, y});

    // clear the timeout if it exists
    clearTimeout(timeout.current as unknown as number);

    // remove the ripple effect after 600ms
    timeout.current = setTimeout(() => setCoords(null), 600);

    onClick?.(e);
  };

  useEffect(() => {
    return () => {
      clearTimeout(timeout.current as unknown as number);
    };
  }, []);

  const renderRippleEffect = (coords: {x: number; y: number}) => (
    <div
      key={`${coords.x},${coords.y}`}
      className="absolute h-8 w-8 rounded-full bg-white/60 opacity-100"
      style={{
        animation: "ripple 600ms linear",
        left: coords.x - 15,
        top: coords.y - 15,
      }}
    />
  );

  return (
    <button
      type={type}
      disabled={disabled || isPending}
      onClick={onButtonClick}
      {...rest}
      className={[
        "disabled:text-outline disabled:border-outline-variant disabled:fill-outline disabled:cursor-not-allowed disabled:text-shadow-none",
        "relative inline-flex items-center justify-center rounded-full hover:cursor-pointer",
        "focus-visible:ring-outline ring-offset-surface-bright bg-clip-padding transition-colors focus:outline-none focus-visible:ring-2",
        variant === "primary"
          ? "bg-primary-container text-on-primary-container hover:bg-primary-container/90 border-primary-container hover:border-primary-container/90 fill-on-primary-container border text-shadow-sm"
          : "",
        variant === "secondary"
          ? "bg-secondary-container hover:bg-secondary-container/90 border-secondary-container hover:border-secondary-container/90 border fill-white text-white text-shadow-sm"
          : "",
        variant === "white"
          ? "border-outline border bg-white fill-[#333] text-[#333] hover:bg-[#eee]"
          : "",
        variant === "facebook"
          ? "border border-[#4267b2] bg-[#4267b2] fill-white text-white hover:border-[#4267b2]/90 hover:bg-[#4267b2]/90"
          : "",
        variant === "outlined"
          ? "hover:border-primary-container fill-on-surface-variant text-on-surface-variant border-outline hover:text-primary-container hover:fill-primary-container border"
          : "",
        variant === "text"
          ? "hover:border-primary-container hover:text-primary-container hover:fill-primary-container py-g-1 underline underline-offset-2 disabled:no-underline"
          : "disabled:bg-outline-variant",
        variant === "icon" ? "xs:-m-3 xs:p-3 -m-2 rounded-full p-2" : "",
        variant !== "icon" && variant !== "text"
          ? "px-g-3 py-g-1 focus-visible:ring-offset-2"
          : "",
        disabledRippleEffect ? "" : "overflow-hidden",
        className,
      ].join(" ")}
    >
      {!disabledRippleEffect &&
        coords &&
        !["text", "icon"].includes(variant) &&
        renderRippleEffect(coords)}
      <span className="relative w-full">
        <span
          className={`flex items-center gap-3 ${isPending ? "opacity-0" : "leading-none"} ${
            centerText ? "justify-center" : "justify-start"
          }`}
        >
          {children}
        </span>
        {isPending && (
          <div className="spinner absolute inset-0 mx-auto -my-1"></div>
        )}
      </span>
    </button>
  );
};

export default memo(RippleButton);
